import type { TRootState } from 'slices/store';
import { collectOrderedFieldIds } from 'utils/objectUtils';
import type { TType } from '@timeedit/types';

type TTypeMap = TRootState['objectTypes']['map'];

const fieldIdsOf = (type: TType | undefined) => [
  ...(type?.parentFields ?? []),
  ...(type?.fields ?? []),
];

// A type whose own and inherited fields include every field of the given types, if any.
// Most multi-type objects have one: the other types are ancestors or subsets of it.
export const coveringTypeId = (
  typeIds: readonly number[],
  typeMap: TTypeMap
): number | undefined => {
  const union = new Set(typeIds.flatMap((id) => fieldIdsOf(typeMap[id])));
  return typeIds.find((id) => {
    const own = new Set(fieldIdsOf(typeMap[id]));
    return [...union].every((fieldId) => own.has(fieldId));
  });
};

// Field ids of the given types in global order. A covering type's fetched order is already
// the answer; otherwise the order across all fields is needed, or undefined if not loaded.
export const orderedFieldIdsForTypes = (
  typeIds: readonly number[],
  typeMap: TTypeMap,
  orderByType: Record<number, number[] | undefined>,
  globalOrder: { id: number }[]
): number[] | undefined => {
  const covering = coveringTypeId(typeIds, typeMap);
  const fetched = covering == null ? undefined : orderByType[covering];
  if (fetched) return fetched;
  if (globalOrder.length > 0) {
    return collectOrderedFieldIds(typeIds, typeMap, globalOrder);
  }
  return undefined;
};
